/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class Store {
    constructor() {
        this.data = new Map();
    }
    static get Instance() {
        if (!Store.instance) {
            Store.instance = new Store();
        }
        return Store.instance;
    }
    get(key) {
        return this.data.get(key);
    }
    set(key, value) {
        this.data.set(key, value);
    }
    setConfig(value) {
        this.data.set("config", value);
    }
    getConfig() {
        return this.data.get("config");
    }
    getApiBaseUrl() {
        const domain = this.getConfig().domain;
        if (domain.startsWith("localhost")) {
            return `http://${domain}`;
        }
        return `https://${domain}`;
    }
    remove(key) {
        this.data.delete(key);
    }
    clear() {
        this.data.clear();
    }
}
const store = Store.Instance;

class LoginflowError extends Error {
    constructor(code, message, details) {
        // eslint-disable-line @typescript-eslint/no-explicit-any
        super(message);
        this.name = "LoginflowError";
        this.code = code;
        this.details = details;
    }
}

class HttpClient {
    constructor() { }
    static get Instance() {
        if (!HttpClient.instance) {
            HttpClient.instance = new HttpClient();
        }
        return HttpClient.instance;
    }
    request(endpoint_1) {
        return __awaiter(this, arguments, void 0, function* (endpoint, options = {}) {
            const config = store.getConfig();
            const url = `${store.getApiBaseUrl()}${endpoint}`;
            const timeout = 50000;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);
            try {
                const response = yield fetch(url, Object.assign(Object.assign({}, options), { signal: controller.signal, headers: Object.assign(Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (config.apiKey && { 'Authorization': `Bearer ${config.apiKey}` })), config.headers), options.headers) }));
                clearTimeout(timeoutId);
                if (!response.ok) {
                    const errorData = yield response.json().catch(() => ({}));
                    throw new LoginflowError(errorData.code || 'API_ERROR', errorData.message || `HTTP ${response.status}: ${response.statusText}`, errorData.details);
                }
                return yield response.json();
            }
            catch (error) {
                clearTimeout(timeoutId);
                if (error instanceof LoginflowError) {
                    throw error;
                }
                if (error instanceof Error) {
                    if (error.name === 'AbortError') {
                        throw new LoginflowError('TIMEOUT', 'Request timeout');
                    }
                    throw new LoginflowError('NETWORK_ERROR', error.message);
                }
                throw new LoginflowError('UNKNOWN_ERROR', 'An unknown error occurred');
            }
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    post(endpoint, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(endpoint, {
                credentials: 'include',
                method: 'POST',
                body: data ? JSON.stringify(data) : undefined,
            });
        });
    }
    get(endpoint) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(endpoint, {
                credentials: 'include',
                method: 'GET',
            });
        });
    }
}

class SessionService {
    constructor() {
        this.AUTH_SESSION_STORAGE_KEY = 'saasbase_auth_session';
        this.refreshTimeoutId = null;
        this.refreshPromise = null;
    }
    static get Instance() {
        if (!SessionService.instance) {
            SessionService.instance = new SessionService();
        }
        return SessionService.instance;
    }
    setCurrentSession(session) {
        localStorage.setItem(this.AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
    }
    getCurrentSession() {
        const session = this.getStoredSession();
        if (!session) {
            return null;
        }
        if (this.isSessionExpired(session)) {
            this.clearSession();
            return null;
        }
        // Check if we should refresh proactively (when 80% of token lifetime has passed)
        if (this.shouldRefreshToken(session)) {
            this.triggerBackgroundRefresh(session);
        }
        return session;
    }
    isAuthenticated() {
        return this.getCurrentSession() !== null;
    }
    getAuthenticatedUser() {
        const session = this.getCurrentSession();
        if (!session || !session.id_token) {
            return null;
        }
        const decodedToken = this.decodeJWT(session.id_token);
        if (!decodedToken) {
            return null;
        }
        return decodedToken;
    }
    cleanup() {
        // Call this method when unmounting/destroying the SDK instance
        if (this.refreshTimeoutId) {
            clearTimeout(this.refreshTimeoutId);
            this.refreshTimeoutId = null;
        }
        // if (this.redirectTimeoutId) {
        //   clearTimeout(this.redirectTimeoutId);
        //   this.redirectTimeoutId = null;
        // }
        this.refreshPromise = null;
    }
    clearSession() {
        localStorage.removeItem(this.AUTH_SESSION_STORAGE_KEY);
    }
    isSessionExpired(session) {
        if (!session || !session.expires_at) {
            return true;
        }
        return Date.now() >= session.expires_at;
    }
    cleanExpiredSession() {
        const session = this.getStoredSession();
        if (session && this.isSessionExpired(session)) {
            this.clearSession();
        }
    }
    getStoredSession() {
        const storedSession = localStorage.getItem(this.AUTH_SESSION_STORAGE_KEY);
        if (!storedSession) {
            return null;
        }
        try {
            return JSON.parse(storedSession);
        }
        catch (_a) {
            // Invalid JSON, clean it up
            this.clearSession();
            return null;
        }
    }
    decodeJWT(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join(''));
            return JSON.parse(jsonPayload);
        }
        catch (_a) {
            return null;
        }
    }
    shouldRefreshToken(session) {
        if (!session || !session.expires_at || !session.refresh_token) {
            return false;
        }
        const now = Date.now();
        const expiresAt = session.expires_at;
        const expiresIn = session.expires_in || 3600;
        // Refresh when 80% of the token lifetime has passed
        const refreshThreshold = now + (expiresIn * 1000 * 0.2);
        return expiresAt <= refreshThreshold;
    }
    scheduleTokenRefresh() {
        // Clear any existing timeout
        if (this.refreshTimeoutId) {
            clearTimeout(this.refreshTimeoutId);
            this.refreshTimeoutId = null;
        }
        const session = this.getCurrentSession();
        if (!session || !session.expires_at || !session.refresh_token) {
            return;
        }
        const now = Date.now();
        const expiresAt = session.expires_at;
        const expiresIn = session.expires_in || 3600;
        // Schedule refresh at 80% of token lifetime
        const timeUntilRefresh = (expiresAt - now) - (expiresIn * 1000 * 0.2);
        if (timeUntilRefresh > 0) {
            this.refreshTimeoutId = setTimeout(() => {
                this.triggerBackgroundRefresh(session);
            }, timeUntilRefresh);
        }
    }
    triggerBackgroundRefresh(session) {
        // Avoid multiple concurrent refresh attempts
        if (this.refreshPromise) {
            return;
        }
        // Don't refresh if no refresh token
        if (!session.refresh_token) {
            return;
        }
        this.refreshPromise = this.refreshTokens(session.refresh_token)
            .then((newSession) => {
            if (newSession) {
                // Store the new tokens
                this.setCurrentSession(newSession);
                // Schedule the next refresh
                this.scheduleTokenRefresh();
            }
            return newSession;
        })
            .catch((error) => {
            console.error('Background token refresh failed:', error);
            return null;
        })
            .finally(() => {
            this.refreshPromise = null;
        });
    }
    refreshTokens(refreshToken) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield HttpClient.Instance.post('/login-flow/v1/refresh', {
                    refresh_token: refreshToken
                });
                return {
                    id_token: response.id_token,
                    access_token: response.access_token,
                    refresh_token: response.refresh_token,
                    expires_in: response.expires_in,
                    expires_at: Date.now() + (response.expires_in * 1000)
                };
            }
            catch (error) {
                if (error instanceof LoginflowError) {
                    console.error('Token refresh failed:', error.message, error.details);
                }
                else {
                    console.error('Token refresh failed:', error);
                }
                // Clear the session if refresh fails
                this.clearSession();
                return null;
            }
        });
    }
}

/**
 * Centralized manager for flow state in sessionStorage.
 * Handles reading, writing, validation, and expiration of flow state.
 */
class FlowStateManager {
    constructor(storage = sessionStorage) {
        this.storage = storage;
        this.FLOW_STATE_KEY = 'saasbase_loginflow_state';
        this.FLOW_EXPIRY_MS = 9 * 60 * 1000; // 9 minutes
    }
    /**
     * Get the current flow state from storage.
     * Returns null if state doesn't exist or has expired.
     */
    getFlowState() {
        const storedFlow = this.storage.getItem(this.FLOW_STATE_KEY);
        if (!storedFlow) {
            return null;
        }
        try {
            const flowState = JSON.parse(storedFlow);
            if (this.isExpired(flowState)) {
                this.clearFlowState();
                return null;
            }
            return flowState;
        }
        catch (_a) {
            this.clearFlowState();
            return null;
        }
    }
    /**
     * Save flow state to storage.
     */
    saveFlowState(flowType, flow_id, response) {
        const now = Date.now();
        const existingState = this.getFlowState();
        const flowState = {
            flowType,
            flow_id,
            state: response.state,
            startedAt: (existingState === null || existingState === void 0 ? void 0 : existingState.startedAt) || now,
            lastUpdatedAt: now,
            render_spec: response.render_spec
        };
        this.storage.setItem(this.FLOW_STATE_KEY, JSON.stringify(flowState));
    }
    /**
     * Restore flow state from browser history state.
     * This is used when navigating back/forward to restore the flow state from history.
     * The restored state allows the SDK to resume the existing backend session.
     */
    restoreFromHistory(historyState) {
        const flowState = {
            flowType: historyState.flowType,
            flow_id: historyState.flow_id,
            state: historyState.state,
            startedAt: historyState.timestamp,
            lastUpdatedAt: historyState.timestamp,
            render_spec: undefined // Will be fetched during resume
        };
        this.storage.setItem(this.FLOW_STATE_KEY, JSON.stringify(flowState));
    }
    /**
     * Clear flow state from storage.
     */
    clearFlowState() {
        this.storage.removeItem(this.FLOW_STATE_KEY);
    }
    /**
     * Check if a flow state has expired.
     */
    isExpired(flowState) {
        const now = Date.now();
        return now - flowState.lastUpdatedAt > this.FLOW_EXPIRY_MS;
    }
    /**
     * Get the expiry time in milliseconds.
     */
    get expiryMs() {
        return this.FLOW_EXPIRY_MS;
    }
}

/**
 * Utility for detecting flow type based on URL path.
 */
class FlowTypeDetector {
    /**
     * Detect flow type from current URL path.
     */
    static detect(config, location = window.location) {
        const path = location.pathname.toLowerCase();
        if (path.includes(config.signinUrl)) {
            return 'signin';
        }
        if (path.includes(config.signupUrl)) {
            return 'signup';
        }
        return 'custom';
    }
}

/**
 * Simple event bus for flow-related events.
 * Enables loose coupling between Loginflow and NavigationManager.
 */
class FlowEventBus {
    constructor() {
        this.listeners = new Map();
    }
    /**
     * Subscribe to a flow event.
     */
    on(event, handler) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(handler);
    }
    /**
     * Unsubscribe from a flow event.
     */
    off(event, handler) {
        var _a;
        (_a = this.listeners.get(event)) === null || _a === void 0 ? void 0 : _a.delete(handler);
    }
    /**
     * Emit a flow event to all subscribers.
     */
    emit(event, data) {
        const handlers = this.listeners.get(event);
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(data);
                }
                catch (error) {
                    console.error(`[FlowEventBus] Error in event handler for ${event}:`, error);
                }
            });
        }
    }
    /**
     * Remove all listeners.
     */
    clear() {
        this.listeners.clear();
    }
    /**
     * Remove all listeners for a specific event.
     */
    clearEvent(event) {
        this.listeners.delete(event);
    }
}

/**
 * Browser implementation using Performance Navigation Timing API.
 */
class BrowserNavigationTypeDetector {
    getNavigationType() {
        // Use the modern Navigation Timing API Level 2
        const navEntries = performance.getEntriesByType('navigation');
        if (navEntries.length > 0) {
            return navEntries[0].type;
        }
        // Fallback to deprecated API for older browsers
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const legacyNav = performance.navigation;
        if (legacyNav) {
            // TYPE_NAVIGATE = 0, TYPE_RELOAD = 1, TYPE_BACK_FORWARD = 2
            switch (legacyNav.type) {
                case 1:
                    return 'reload';
                case 2:
                    return 'back_forward';
                default:
                    return 'navigate';
            }
        }
        // Default fallback if neither API is available
        return 'navigate';
    }
}

class Loginflow {
    constructor(flowId, flowEventBus, location = window.location, flowStateManager, navigationTypeDetector) {
        this.httpClient = HttpClient.Instance;
        this.redirectTimeoutId = null;
        // Event handlers map for client-side events
        this.eventHandlers = new Map([
            ["switch_to_signin", this.handleSwitchToSignin.bind(this)],
            ["switch_to_signup", this.handleSwitchToSignup.bind(this)],
        ]);
        const config = store.getConfig();
        if (!config.domain) {
            throw new LoginflowError("CONFIG_ERROR", "Domain is required");
        }
        if (!config.appId) {
            throw new LoginflowError("CONFIG_ERROR", "App ID is required");
        }
        if (!config.clientSecret) {
            throw new LoginflowError("CONFIG_ERROR", "Client Secret is required");
        }
        this.flowId = flowId;
        this.location = location;
        this.flowStateManager = flowStateManager || new FlowStateManager();
        this.flowEventBus = flowEventBus || new FlowEventBus();
        this.navigationTypeDetector =
            navigationTypeDetector || new BrowserNavigationTypeDetector();
        // Check and clean expired auth on initialization
        SessionService.Instance.cleanExpiredSession();
        // Schedule refresh for existing valid tokens
        SessionService.Instance.scheduleTokenRefresh();
    }
    static init(flowId, flowEventBus, location, flowStateManager, navigationTypeDetector) {
        return __awaiter(this, void 0, void 0, function* () {
            // Create SDK instance
            const sdk = new Loginflow(flowId, flowEventBus, location, flowStateManager, navigationTypeDetector);
            // Initialize flow automatically
            const flowResponse = yield sdk.initializeFlowInternal();
            return {
                sdk,
                flowType: flowResponse.flowType,
                isResumed: flowResponse.isResumed,
                state: flowResponse.state,
                render_spec: flowResponse.render_spec,
                auth_result: flowResponse.auth_result,
            };
        });
    }
    /**
     * Start a new flow with the server.
     */
    startFlow(flowType, request) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!request.app_id) {
                throw new LoginflowError("VALIDATION_ERROR", "Client ID is required");
            }
            const endpoint = `/v1/auth-flow/${flowType}/start`;
            return this.executeFlowRequest(() => this.httpClient.post(endpoint, request), "START_FLOW_ERROR", "Failed to start flow");
        });
    }
    /**
     * Resume an existing flow with the server.
     */
    resumeFlow(flowType) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/v1/auth-flow/${flowType}/resume`;
            return this.executeFlowRequest(() => this.httpClient.post(endpoint), "RESUME_FLOW_ERROR", "Failed to resume flow");
        });
    }
    /**
     * Process an event in the flow.
     */
    processEvent(request, beforeFinishCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!request.event) {
                throw new LoginflowError("VALIDATION_ERROR", "Event type is required");
            }
            // Check for client-side event handlers
            const handler = this.eventHandlers.get(request.event);
            if (handler) {
                return yield handler();
            }
            // Server-side event processing
            return yield this.processServerEvent(request, beforeFinishCallback);
        });
    }
    /**
     * Execute a flow request with consistent error handling and side effects.
     */
    executeFlowRequest(operation, errorCode, errorMessage, beforeFinishCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield operation();
                if (beforeFinishCallback) {
                    yield beforeFinishCallback(response);
                }
                this.processAuthResult(response.auth_result);
                if (response.redirection) {
                    this.redirect(response.redirection);
                }
                return response;
            }
            catch (error) {
                if (error instanceof LoginflowError) {
                    throw error;
                }
                throw new LoginflowError(errorCode, errorMessage, error instanceof Error
                    ? { originalError: error.message, stack: error.stack }
                    : error);
            }
        });
    }
    /**
     * Process server-side events.
     */
    processServerEvent(request, beforeFinishCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            // Check if flow state has expired (9 minutes timeout)
            let flowState = this.flowStateManager.getFlowState();
            if (!flowState) {
                // Flow state expired - restart the flow to get a fresh server session
                console.warn("Flow state expired, restarting flow before processing event");
                yield this.initializeFlowInternal();
                // Get the newly created flow state
                flowState = this.flowStateManager.getFlowState();
                if (!flowState) {
                    throw new LoginflowError("FLOW_RESTART_ERROR", "Failed to restart flow - no flow state after initialization");
                }
            }
            const previousState = flowState.state;
            const endpoint = `/v1/auth-flow/${flowState.flowType}/events`;
            const response = yield this.executeFlowRequest(() => this.httpClient.post(endpoint, request), "PROCESS_EVENT_ERROR", "Failed to process event", beforeFinishCallback);
            // Only save flow state if authentication is not complete
            // If auth_result exists, processAuthResult already cleared the state
            if (!response.auth_result) {
                this.flowStateManager.saveFlowState(flowState.flowType, flowState.flow_id, response);
                // Emit event to notify listeners (e.g., NavigationManager)
                const updatedFlowState = this.flowStateManager.getFlowState();
                if (updatedFlowState) {
                    const isNewStep = previousState !== response.state;
                    this.flowEventBus.emit("flow:updated", {
                        flowState: updatedFlowState,
                        isNewStep,
                    });
                }
            }
            return response;
        });
    }
    /**
     * Handle successful authentication.
     */
    processAuthResult(authResult) {
        if (authResult) {
            const authData = {
                id_token: authResult.id_token,
                access_token: authResult.access_token,
                refresh_token: authResult.refresh_token,
                expires_in: authResult.expires_in,
                expires_at: Date.now() + authResult.expires_in * 1000,
            };
            SessionService.Instance.setCurrentSession(authData);
            // Schedule the next refresh
            SessionService.Instance.scheduleTokenRefresh();
            // Clear flow states when authentication is successful
            this.flowStateManager.clearFlowState();
            // Reload the page to redirect to the configured home page
            this.reloadPage();
        }
    }
    /**
     * Initialize or resume the flow.
     */
    initializeFlowInternal() {
        return __awaiter(this, void 0, void 0, function* () {
            // Determine current flow type (from config or auto-detect)
            const config = store.getConfig();
            const currentFlowType = FlowTypeDetector.detect(config, this.location);
            this.validateFlowType(currentFlowType);
            // Check for existing flow state
            const existingFlowState = this.flowStateManager.getFlowState();
            if (this.shouldResumeFlow(existingFlowState, currentFlowType)) {
                return yield this.resumeExistingFlow(existingFlowState, currentFlowType);
            }
            return yield this.startNewFlow(currentFlowType);
        });
    }
    /**
     * Validate flow type requirements.
     */
    validateFlowType(flowType) {
        if (flowType === "custom" && !this.flowId) {
            throw new LoginflowError("FLOW_ID_ERROR", "Flow ID is required for custom flows");
        }
    }
    /**
     * Check if we should resume an existing flow.
     */
    shouldResumeFlow(existingState, currentFlowType) {
        if (!existingState) {
            return false;
        }
        // Check for explicit resume query parameter from backend redirects
        const urlParams = new URLSearchParams(this.location.search);
        const explicitResume = urlParams.get("resume") === "true";
        // Check navigation type - only resume for reload or back_forward
        // For 'navigate' (fresh link/URL entry), start a new flow unless explicitly told to resume
        const navType = this.navigationTypeDetector.getNavigationType();
        if (navType === "navigate" && !explicitResume) {
            this.flowStateManager.clearFlowState();
            console.log(`Fresh navigation detected (type: ${navType}), starting new flow`);
            return false;
        }
        // Check if flow type has changed
        if (existingState.flowType !== currentFlowType) {
            this.flowStateManager.clearFlowState();
            console.log(`Flow type changed from ${existingState.flowType} to ${currentFlowType}, starting new flow, flow_id: ${this.flowId}`);
            return false;
        }
        // Check if flow_id has changed for custom flows
        if (currentFlowType === "custom" && existingState.flow_id !== this.flowId) {
            this.flowStateManager.clearFlowState();
            console.log(`Custom flow ID changed, starting new flow, flow_id: ${this.flowId}`);
            return false;
        }
        const resumeReason = explicitResume
            ? "explicit resume=true"
            : `navigation type: ${navType}`;
        console.log(`Resuming existing flow (${resumeReason})`);
        return true;
    }
    /**
     * Resume an existing flow.
     */
    resumeExistingFlow(existingState, flowType) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield this.resumeFlow(flowType);
                // Only save flow state if authentication is not complete
                if (!response.auth_result) {
                    this.flowStateManager.saveFlowState(flowType, existingState.flow_id, response);
                    // Emit event to notify listeners
                    const flowState = this.flowStateManager.getFlowState();
                    if (flowState) {
                        this.flowEventBus.emit("flow:resumed", { flowState });
                    }
                }
                return {
                    flowType,
                    isResumed: true,
                    state: response.state,
                    render_spec: response.render_spec,
                    auth_result: response.auth_result,
                };
            }
            catch (error) {
                // If resume fails, clear the state and start fresh
                this.flowStateManager.clearFlowState();
                console.warn("Failed to resume flow, starting fresh:", error);
                return yield this.startNewFlow(flowType);
            }
        });
    }
    /**
     * Start a new flow.
     */
    startNewFlow(flowType) {
        return __awaiter(this, void 0, void 0, function* () {
            const config = store.getConfig();
            const urlParams = new URLSearchParams(this.location.search);
            // convert url params to an object for metadata
            const metadata = {};
            urlParams.forEach((value, key) => {
                metadata[key] = value;
            });
            console.log("metadata", metadata);
            // Build start flow request
            const startFlowRequest = {
                app_id: config.appId,
                client_secret: config.clientSecret,
                origin_url: this.location.href,
                signin_url: config.signinUrl,
                signup_url: config.signupUrl,
                locale: config.locale || "en",
                flow_id: this.determineFlowId(flowType),
                metadata: metadata,
            };
            const response = yield this.startFlow(flowType, startFlowRequest);
            // Only save flow state if authentication is not complete
            if (!response.auth_result) {
                this.flowStateManager.saveFlowState(flowType, startFlowRequest.flow_id, response);
                // Emit event to notify listeners
                const flowState = this.flowStateManager.getFlowState();
                if (flowState) {
                    this.flowEventBus.emit("flow:started", { flowState });
                }
            }
            return {
                flowType,
                isResumed: false,
                state: response.state,
                render_spec: response.render_spec,
                auth_result: response.auth_result,
            };
        });
    }
    /**
     * Determine the flow_id based on flow type.
     */
    determineFlowId(flowType) {
        switch (flowType) {
            case "signin":
                return this.flowId || "signin-flow";
            case "signup":
                return this.flowId || "signup-flow";
            case "custom":
                if (!this.flowId) {
                    throw new LoginflowError("FLOW_ID_ERROR", "Flow ID is required for custom flows");
                }
                return this.flowId;
        }
    }
    /**
     * Handle switch to signin event.
     */
    handleSwitchToSignin() {
        return __awaiter(this, void 0, void 0, function* () {
            const config = store.getConfig();
            this.redirect({ url: config.signinUrl, redirect_delay: 0 });
            return { state: "End" };
        });
    }
    /**
     * Handle switch to signup event.
     */
    handleSwitchToSignup() {
        return __awaiter(this, void 0, void 0, function* () {
            const config = store.getConfig();
            this.redirect({ url: config.signupUrl, redirect_delay: 0 });
            return { state: "End" };
        });
    }
    /**
     * Redirect to a URL.
     */
    redirect(rd) {
        if (!rd.url) {
            return;
        }
        // Clear any existing redirect timeout before scheduling a new one
        // This handles cases where navigation (like browser back) cancels a pending redirect
        if (this.redirectTimeoutId !== null) {
            console.log("Clearing existing redirect timeout before scheduling new redirect to:", rd.url);
            clearTimeout(this.redirectTimeoutId);
            this.redirectTimeoutId = null;
        }
        // Redirect URL inside a timeout to ensure the page is rendered.
        // It guarantees that all code in the current execution context finishes first,
        // even though the delay is "zero". It's not about time—it's about execution order in the event loop.
        this.redirectTimeoutId = setTimeout(() => {
            this.location.href = rd.url;
        }, rd.redirect_delay * 1000);
    }
    /**
     * Reload the current page.
     */
    reloadPage() {
        // Defer reload to ensure sessionStorage operations complete
        // This guarantees that all code in the current execution context finishes first
        setTimeout(() => {
            this.location.reload();
        }, 0);
    }
    /**
     * Get the event bus for external listeners.
     */
    getEventBus() {
        return this.flowEventBus;
    }
    /**
     * Cleanup resources.
     */
    cleanup() {
        // Cancel any pending redirect
        if (this.redirectTimeoutId) {
            clearTimeout(this.redirectTimeoutId);
            this.redirectTimeoutId = null;
        }
        // Clear event bus
        this.flowEventBus.clear();
    }
}

/**
 * Utility class for debouncing function calls.
 * Ensures a function is only called after a specified delay has passed since the last invocation.
 */
class Debouncer {
    constructor(delayMs) {
        this.delayMs = delayMs;
        this.timerId = null;
    }
    /**
     * Debounce a function call.
     * If called again before delay expires, the previous call is cancelled.
     */
    debounce(fn) {
        if (this.timerId) {
            clearTimeout(this.timerId);
        }
        this.timerId = setTimeout(() => {
            fn();
            this.timerId = null;
        }, this.delayMs);
    }
    /**
     * Cancel any pending debounced call.
     */
    cancel() {
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
    }
    /**
     * Check if a debounced call is pending.
     */
    isPending() {
        return this.timerId !== null;
    }
}

/**
 * Adapter interfaces for browser APIs to enable dependency injection and testing.
 */
/**
 * Default browser implementation of HistoryAdapter.
 */
class BrowserHistoryAdapter {
    pushState(state, title, url) {
        history.pushState(state, title, url);
    }
    replaceState(state, title, url) {
        history.replaceState(state, title, url);
    }
    get state() {
        return history.state;
    }
}

/**
 * Manages browser history integration for the authentication flow.
 * Listens to flow events via event bus and updates browser history accordingly.
 * Handles popstate events (browser back/forward) and triggers flow restart when needed.
 */
class NavigationManager {
    constructor(flowEventBus, onFlowRestartNeeded, skipHistoryTracking = false, location = window.location, flowStateManager, historyAdapter) {
        this.popstateListener = null;
        this.pageshowListener = null;
        this.isProcessing = false;
        this.isHandlingNavigationEvent = false;
        this.onFlowRestartNeeded = null;
        this.onFlowRestartNeeded = onFlowRestartNeeded;
        this.skipHistoryTracking = skipHistoryTracking;
        this.debouncer = new Debouncer(200);
        this.location = location;
        this.flowStateManager = flowStateManager || new FlowStateManager();
        this.historyAdapter = historyAdapter || new BrowserHistoryAdapter();
        // Subscribe to flow events
        this.subscribeToFlowEvents(flowEventBus);
        // Initialize popstate listener
        this.initializeListener();
    }
    /**
     * Subscribe to flow events from the event bus.
     */
    subscribeToFlowEvents(flowEventBus) {
        flowEventBus.on('flow:started', this.handleFlowStart.bind(this));
        flowEventBus.on('flow:updated', this.handleFlowUpdate.bind(this));
        flowEventBus.on('flow:resumed', this.handleFlowResume.bind(this));
    }
    /**
     * Initialize the popstate and pageshow listeners for browser navigation.
     */
    initializeListener() {
        this.popstateListener = (event) => {
            console.log('[NavigationManager] popstate event received');
            // Debounce rapid back/forward clicks
            this.debouncer.debounce(() => {
                console.log('[NavigationManager] Debounce timeout fired, handling popstate');
                void this.handlePopState(event);
            });
        };
        // Handle bfcache (back/forward cache) restoration
        // When a page is restored from bfcache, popstate might not fire reliably
        this.pageshowListener = (event) => {
            if (event.persisted) {
                console.log('[NavigationManager] Page restored from bfcache');
                // Debounce to handle potential race conditions
                this.debouncer.debounce(() => {
                    console.log('[NavigationManager] Handling bfcache restoration');
                    void this.handleBfcacheRestoration();
                });
            }
        };
        window.addEventListener('popstate', this.popstateListener);
        window.addEventListener('pageshow', this.pageshowListener);
    }
    /**
     * Handle browser back/forward navigation.
     */
    handlePopState(event) {
        return __awaiter(this, void 0, void 0, function* () {
            // Prevent concurrent processing
            if (this.isProcessing) {
                console.log('[NavigationManager] Already processing popstate, skipping');
                return;
            }
            console.log('[NavigationManager] Starting popstate processing');
            this.isProcessing = true;
            this.isHandlingNavigationEvent = true;
            try {
                const historyState = event.state;
                const sessionFlowState = this.flowStateManager.getFlowState();
                const config = store.getConfig();
                const urlFlowType = FlowTypeDetector.detect(config, this.location);
                // Restore history state to session storage BEFORE checking if restart is needed
                // This allows the SDK to resume the existing backend session using the flow-type-specific cookie
                if (historyState) {
                    this.restoreHistoryStateToSession(historyState);
                }
                if (this.shouldRestartFlow(historyState, sessionFlowState, urlFlowType)) {
                    yield this.triggerFlowRestart();
                    return;
                }
                // Valid state - restored successfully
                console.log('[NavigationManager] Valid state restored from history');
            }
            finally {
                this.isProcessing = false;
                this.isHandlingNavigationEvent = false;
            }
        });
    }
    /**
     * Handle page restoration from bfcache (back/forward cache).
     * When a page is restored from bfcache, the popstate event might not fire reliably,
     * so we use the pageshow event with event.persisted=true to detect this case.
     */
    handleBfcacheRestoration() {
        return __awaiter(this, void 0, void 0, function* () {
            // Prevent concurrent processing
            if (this.isProcessing) {
                console.log('[NavigationManager] Already processing bfcache restoration, skipping');
                return;
            }
            console.log('[NavigationManager] Starting bfcache restoration processing');
            this.isProcessing = true;
            this.isHandlingNavigationEvent = true;
            try {
                // Get current history state (not from event, since pageshow doesn't provide it)
                const historyState = this.historyAdapter.state;
                const sessionFlowState = this.flowStateManager.getFlowState();
                const config = store.getConfig();
                const urlFlowType = FlowTypeDetector.detect(config, this.location);
                // Restore history state to session storage BEFORE checking if restart is needed
                if (historyState) {
                    this.restoreHistoryStateToSession(historyState);
                }
                if (this.shouldRestartFlow(historyState, sessionFlowState, urlFlowType)) {
                    yield this.triggerFlowRestart();
                    return;
                }
                // Valid state - restored successfully
                console.log('[NavigationManager] Valid state restored from bfcache');
            }
            finally {
                this.isProcessing = false;
                this.isHandlingNavigationEvent = false;
            }
        });
    }
    /**
     * Restore history state to session storage.
     * This is crucial for cookie preservation: when navigating back, we restore the old flow state
     * so that Loginflow.initializeFlowInternal() can resume using the flow-type-specific cookie.
     */
    restoreHistoryStateToSession(historyState) {
        console.log('[NavigationManager] Restoring history state to session storage:', historyState);
        this.flowStateManager.restoreFromHistory(historyState);
    }
    /**
     * Determine if the flow should be restarted based on navigation state.
     * Note: We've already restored historyState to session storage at this point.
     */
    shouldRestartFlow(historyState, _sessionState, urlFlowType) {
        // Check for flow type mismatch between URL and history
        if (historyState && historyState.flowType !== urlFlowType) {
            console.log('[NavigationManager] Flow type changed via navigation:', historyState.flowType, '→', urlFlowType);
            // Clear the restored state since URL doesn't match
            this.flowStateManager.clearFlowState();
            return true;
        }
        // No history state - initial navigation
        if (!historyState) {
            console.log('[NavigationManager] No history state, skipping');
            return false;
        }
        // Check if session has expired based on timestamp
        const now = Date.now();
        if (now - historyState.timestamp > this.flowStateManager.expiryMs) {
            console.log('[NavigationManager] History state expired, clearing and restarting flow');
            this.flowStateManager.clearFlowState();
            return true;
        }
        // Always trigger restart after restoring state
        // The restored session storage will allow Loginflow to resume using the flow-type-specific cookie
        console.log('[NavigationManager] Valid history state restored, triggering restart to resume flow');
        return true;
    }
    /**
     * Trigger flow restart callback.
     */
    triggerFlowRestart() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.onFlowRestartNeeded) {
                yield this.onFlowRestartNeeded();
            }
        });
    }
    /**
     * Create a history state object from flow state.
     */
    createHistoryState(flowState) {
        return {
            flowType: flowState.flowType,
            flow_id: flowState.flow_id,
            state: flowState.state,
            timestamp: Date.now()
        };
    }
    /**
     * Update browser history.
     */
    updateBrowserHistory(flowState, mode) {
        // Skip history tracking if this instance was created with skipHistoryTracking
        if (this.skipHistoryTracking) {
            console.log('[NavigationManager] Skipping history tracking (created from navigation)');
            return;
        }
        const historyState = this.createHistoryState(flowState);
        try {
            if (mode === 'push') {
                this.historyAdapter.pushState(historyState, '', this.location.href);
                console.log('[NavigationManager] Pushed state to history');
            }
            else {
                this.historyAdapter.replaceState(historyState, '', this.location.href);
                console.log('[NavigationManager] Replaced state in history');
            }
        }
        catch (error) {
            console.error('[NavigationManager] Failed to update history:', error);
        }
    }
    /**
     * Handle flow start event.
     */
    handleFlowStart(data) {
        // When starting a flow, ALWAYS replace the current history entry
        // This works with app routers that already changed the URL
        this.updateBrowserHistory(data.flowState, 'replace');
    }
    /**
     * Handle flow update event.
     */
    handleFlowUpdate(data) {
        // If handling navigation, always replace; otherwise respect isNewStep
        const mode = this.isHandlingNavigationEvent || !data.isNewStep ? 'replace' : 'push';
        this.updateBrowserHistory(data.flowState, mode);
    }
    /**
     * Handle flow resume event.
     */
    handleFlowResume(data) {
        // Always replace state when resuming
        this.updateBrowserHistory(data.flowState, 'replace');
    }
    /**
     * Cleanup resources.
     */
    destroy() {
        // Cancel any pending debounced calls
        this.debouncer.cancel();
        // Remove popstate listener
        if (this.popstateListener) {
            window.removeEventListener('popstate', this.popstateListener);
            this.popstateListener = null;
        }
        // Remove pageshow listener
        if (this.pageshowListener) {
            window.removeEventListener('pageshow', this.pageshowListener);
            this.pageshowListener = null;
        }
        // Clear callback
        this.onFlowRestartNeeded = null;
    }
}

/*
* global abort controller to abort all instances of the webauthn
*/
let controller = new AbortController();
class WebAuthnService {
    constructor() {
        this.httpClient = HttpClient.Instance;
        this.capabilities = {
            webauthn: false,
        };
        this.capabilitiesDetected = false;
    }
    detectCapabilities() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.capabilitiesDetected)
                return;
            try {
                if (typeof window.PublicKeyCredential === 'function') {
                    this.capabilities.webauthn = true;
                }
                else {
                    this.capabilities.webauthn = false;
                }
                if (typeof PublicKeyCredential.getClientCapabilities === "function") {
                    const caps = yield PublicKeyCredential.getClientCapabilities();
                    this.capabilities = Object.assign(Object.assign({}, this.capabilities), caps);
                    return;
                }
                if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function") {
                    this.capabilities.userVerifyingPlatformAuthenticator =
                        yield PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                }
                if ((PublicKeyCredential === null || PublicKeyCredential === void 0 ? void 0 : PublicKeyCredential.isConditionalMediationAvailable) !== undefined && typeof PublicKeyCredential.isConditionalMediationAvailable === "function") {
                    this.capabilities.conditionalGet = yield PublicKeyCredential.isConditionalMediationAvailable();
                }
                else {
                    this.capabilities.conditionalGet = false;
                }
            }
            finally {
                this.capabilitiesDetected = true;
            }
        });
    }
    fetchPublicKeyCredentialRequestOptions(flowType, conditional) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const endpoint = `/webauthn/login/options`;
                return this.httpClient.post(endpoint, {
                    conditional,
                    flow_type: flowType,
                });
            }
            catch (error) {
                console.error("error", error);
                return null;
            }
        });
    }
    fetchPublicKeyCredentialCreationOptions(flowType, conditional) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const endpoint = `/webauthn/register/options`;
                return this.httpClient.post(endpoint, {
                    conditional,
                    flow_type: flowType,
                });
            }
            catch (error) {
                console.error("error", error);
                return null;
            }
        });
    }
    startRegistration(opts, useAutoRegister) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.detectCapabilities();
            if (!opts) {
                console.error("No PublicKeyCredentialCreationOptionsJSON provided");
                return null;
            }
            controller.abort();
            controller = new AbortController();
            const options = this.toPublicKeyCredentialCreationOptions(opts);
            const createOptions = {
                signal: controller.signal,
                publicKey: options
            };
            if (useAutoRegister) {
                if (!this.isConditionalCreateAvailable()) {
                    console.error("Conditional create mediation not supported in this browser");
                    return null;
                }
                // @ts-expect-error: `mediation` doesn't yet exist on CredentialCreationOptions but it's possible as of Sept 2024
                createOptions.mediation = 'conditional';
            }
            try {
                const credential = (yield navigator.credentials.create(createOptions));
                if (!credential) {
                    console.error("no credential returned by authenticator");
                    return;
                }
                const { id, rawId, response, type } = credential;
                // Continue to play it safe with `getTransports()` for now, even when L3 types say it's required
                let transports = undefined;
                if (typeof response.getTransports === 'function') {
                    transports = response.getTransports();
                }
                // L3 says this is required, but browser and webview support are still not guaranteed.
                let responsePublicKeyAlgorithm = undefined;
                if (typeof response.getPublicKeyAlgorithm === 'function') {
                    try {
                        responsePublicKeyAlgorithm = response.getPublicKeyAlgorithm();
                    }
                    catch (error) {
                        warnOnBrokenImplementation('getPublicKeyAlgorithm()', error);
                    }
                }
                let responsePublicKey = undefined;
                if (typeof response.getPublicKey === 'function') {
                    try {
                        const _publicKey = response.getPublicKey();
                        if (_publicKey !== null) {
                            responsePublicKey = this.bufferToBase64URLString(_publicKey);
                        }
                    }
                    catch (error) {
                        warnOnBrokenImplementation('getPublicKey()', error);
                    }
                }
                // L3 says this is required, but browser and webview support are still not guaranteed.
                let responseAuthenticatorData;
                if (typeof response.getAuthenticatorData === 'function') {
                    try {
                        responseAuthenticatorData = this.bufferToBase64URLString(response.getAuthenticatorData());
                    }
                    catch (error) {
                        warnOnBrokenImplementation('getAuthenticatorData()', error);
                    }
                }
                return {
                    id,
                    rawId: this.bufferToBase64URLString(rawId),
                    response: {
                        attestationObject: this.bufferToBase64URLString(response.attestationObject),
                        clientDataJSON: this.bufferToBase64URLString(response.clientDataJSON),
                        transports,
                        publicKeyAlgorithm: responsePublicKeyAlgorithm,
                        publicKey: responsePublicKey,
                        authenticatorData: responseAuthenticatorData,
                    },
                    type,
                    clientExtensionResults: credential.getClientExtensionResults(),
                    authenticatorAttachment: this.toAuthenticatorAttachment(credential.authenticatorAttachment),
                };
            }
            catch (error) {
                const errorIdentified = this.identifyAuthenticationError({
                    error: error,
                    options: createOptions,
                });
                if (errorIdentified instanceof WebAuthnError) {
                    console.error(`WebAuthn authentication failed with code: ${errorIdentified.code}, message: ${errorIdentified.message}`);
                }
                else {
                    console.error("WebAuthn authentication failed with error:", error);
                }
                return null;
            }
        });
    }
    startAuthentication(opts, useBrowserAutofill) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.detectCapabilities();
            if (!opts) {
                console.error("No PublicKeyCredentialRequestOptionsJSON provided");
                return null;
            }
            if (!this.isWebAuthnAvailable()) {
                console.error("WebAuthn not supported in this browser");
                return null;
            }
            // Abort any previous authentication attempt
            controller.abort();
            controller = new AbortController();
            const options = this.toPublicKeyCredentialRequestOptions(opts);
            const requestOptions = {
                signal: controller.signal,
                publicKey: options
            };
            if (useBrowserAutofill) {
                if (!this.isConditionalGetAvailable()) {
                    console.error("Conditional mediation not supported in this browser");
                    return null;
                }
                requestOptions.mediation = "conditional";
                if (requestOptions.publicKey) {
                    // in conditional get, allowCredentials must be an empty array to signal the browser to show all available credentials
                    requestOptions.publicKey.allowCredentials = [];
                }
            }
            try {
                const result = yield navigator.credentials.get(requestOptions);
                return this.toPublicKeyCredentialJSON(result);
            }
            catch (error) {
                const errorIdentified = this.identifyAuthenticationError({
                    error: error,
                    options: requestOptions,
                });
                if (errorIdentified instanceof WebAuthnError) {
                    console.error(`WebAuthn authentication failed with code: ${errorIdentified.code}, message: ${errorIdentified.message}`);
                }
                else {
                    console.error("WebAuthn authentication failed with error:", error);
                }
                return null;
            }
        });
    }
    signalUnknownCredential(rpId, credentialId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.detectCapabilities();
            if (this.isSignalUnknownCredentialAvailable()) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    yield PublicKeyCredential.signalUnknownCredential({
                        rpId,
                        credentialId,
                    });
                }
                catch (err) {
                    console.error("Failed to signal unknown credential:", err);
                }
            }
            else {
                console.warn("signalUnknownCredential not supported in this browser");
            }
        });
    }
    signalCurrentUserDetails(details) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.detectCapabilities();
            if (this.isSignalCurrentUserDetailsAvailable()) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    yield PublicKeyCredential.signalCurrentUserDetails(details);
                }
                catch (err) {
                    console.error("Failed to signal current user details:", err);
                }
            }
            else {
                console.warn("signalCurrentUserDetails not supported. Ask user to update manually.");
            }
        });
    }
    toPublicKeyCredentialDescriptor(descriptor) {
        const { id } = descriptor;
        return Object.assign(Object.assign({}, descriptor), { id: this.base64URLStringToBuffer(id), type: descriptor.type, transports: descriptor.transports });
    }
    toAuthenticatorAttachment(attachment) {
        const attachments = ['cross-platform', 'platform'];
        if (!attachment) {
            return;
        }
        if (attachments.indexOf(attachment) < 0) {
            return;
        }
        return attachment;
    }
    bufferToBase64URLString(buffer) {
        const bytes = new Uint8Array(buffer);
        let str = '';
        for (const charCode of bytes) {
            str += String.fromCharCode(charCode);
        }
        const base64String = btoa(str);
        return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }
    base64URLStringToBuffer(base64URLString) {
        // Convert from Base64URL to Base64
        const base64 = base64URLString.replace(/-/g, '+').replace(/_/g, '/');
        const padLength = (4 - (base64.length % 4)) % 4;
        const padded = base64.padEnd(base64.length + padLength, '=');
        const binary = atob(padded);
        const buffer = new ArrayBuffer(binary.length);
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return buffer;
    }
    toPublicKeyCredentialJSON(cred) {
        if (typeof cred.toJSON === "function") {
            return cred.toJSON();
        }
        const response = cred.response;
        return {
            id: cred.id,
            type: cred.type,
            rawId: this.bufferToBase64URLString(cred.rawId),
            authenticatorAttachment: this.toAuthenticatorAttachment(cred.authenticatorAttachment),
            clientExtensionResults: cred === null || cred === void 0 ? void 0 : cred.getClientExtensionResults(),
            response: {
                clientDataJSON: this.bufferToBase64URLString(response.clientDataJSON),
                authenticatorData: this.bufferToBase64URLString(response.authenticatorData),
                signature: this.bufferToBase64URLString(response.signature),
                userHandle: response.userHandle ? this.bufferToBase64URLString(response.userHandle) : undefined,
            },
        };
    }
    toPublicKeyCredentialCreationOptions(optionJSON) {
        var _a;
        if (typeof PublicKeyCredential.parseCreationOptionsFromJSON === "function") {
            return PublicKeyCredential.parseCreationOptionsFromJSON(optionJSON);
        }
        const publicKey = {
            challenge: this.base64URLStringToBuffer(optionJSON.challenge),
            rp: {
                id: optionJSON.rp.id,
                name: optionJSON.rp.name
            },
            user: {
                displayName: optionJSON.user.displayName,
                id: this.base64URLStringToBuffer(optionJSON.user.id),
                name: optionJSON.user.name
            },
            pubKeyCredParams: optionJSON.pubKeyCredParams.map((p) => ({
                alg: p.alg,
                type: p.type
            })),
            timeout: optionJSON.timeout,
        };
        if (optionJSON.attestation) {
            publicKey.attestation = optionJSON.attestation;
        }
        if (optionJSON.extensions !== undefined) {
            publicKey.extensions = optionJSON.extensions;
        }
        if (optionJSON.authenticatorSelection) {
            const authenticatorSelection = {};
            if (optionJSON.authenticatorSelection.authenticatorAttachment) {
                authenticatorSelection.authenticatorAttachment = optionJSON.authenticatorSelection.authenticatorAttachment;
            }
            if (optionJSON.authenticatorSelection.requireResidentKey) {
                authenticatorSelection.requireResidentKey = optionJSON.authenticatorSelection.requireResidentKey;
            }
            if (optionJSON.authenticatorSelection.residentKey) {
                authenticatorSelection.residentKey = optionJSON.authenticatorSelection.residentKey;
            }
            if (optionJSON.authenticatorSelection.userVerification) {
                authenticatorSelection.userVerification = optionJSON.authenticatorSelection.userVerification;
            }
            publicKey.authenticatorSelection = authenticatorSelection;
        }
        if ((_a = optionJSON.excludeCredentials) === null || _a === void 0 ? void 0 : _a.length) {
            publicKey.excludeCredentials = optionJSON.excludeCredentials.map((desc) => this.toPublicKeyCredentialDescriptor(desc));
        }
        return publicKey;
    }
    toPublicKeyCredentialRequestOptions(optionJSON) {
        var _a;
        if (typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function") {
            return PublicKeyCredential.parseRequestOptionsFromJSON(optionJSON);
        }
        const publicKey = {
            challenge: this.base64URLStringToBuffer(optionJSON.challenge),
        };
        if (optionJSON.rpId !== undefined) {
            publicKey.rpId = optionJSON.rpId;
        }
        if (optionJSON.timeout !== undefined) {
            publicKey.timeout = optionJSON.timeout;
        }
        if (optionJSON.userVerification !== undefined) {
            publicKey.userVerification = optionJSON.userVerification;
        }
        if (optionJSON.extensions !== undefined) {
            publicKey.extensions = optionJSON.extensions;
        }
        if ((_a = optionJSON.allowCredentials) === null || _a === void 0 ? void 0 : _a.length) {
            publicKey.allowCredentials = optionJSON.allowCredentials.map((desc) => this.toPublicKeyCredentialDescriptor(desc));
        }
        return publicKey;
    }
    identifyAuthenticationError({ error, options, }) {
        const { publicKey } = options;
        if (!publicKey) {
            throw Error('options was missing required publicKey property');
        }
        if (error.name === 'AbortError') {
            if (options.signal instanceof AbortSignal) {
                // https://www.w3.org/TR/webauthn-2/#sctn-createCredential (Step 16)
                return new WebAuthnError({
                    message: 'Authentication ceremony was sent an abort signal',
                    code: 'ERROR_CEREMONY_ABORTED',
                    cause: error,
                });
            }
        }
        else if (error.name === 'NotAllowedError') {
            /**
             * Pass the error directly through. Platforms are overloading this error beyond what the spec
             * defines and we don't want to overwrite potentially useful error messages.
             */
            return new WebAuthnError({
                message: error.message,
                code: 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY',
                cause: error,
            });
        }
        else if (error.name === 'SecurityError') {
            const effectiveDomain = window.location.hostname;
            if (!isValidDomain(effectiveDomain)) {
                // https://www.w3.org/TR/webauthn-2/#sctn-discover-from-external-source (Step 5)
                return new WebAuthnError({
                    message: `${window.location.hostname} is an invalid domain`,
                    code: 'ERROR_INVALID_DOMAIN',
                    cause: error,
                });
            }
            else if (publicKey.rpId !== effectiveDomain) {
                // https://www.w3.org/TR/webauthn-2/#sctn-discover-from-external-source (Step 6)
                return new WebAuthnError({
                    message: `The RP ID "${publicKey.rpId}" is invalid for this domain`,
                    code: 'ERROR_INVALID_RP_ID',
                    cause: error,
                });
            }
        }
        else if (error.name === 'UnknownError') {
            // https://www.w3.org/TR/webauthn-2/#sctn-op-get-assertion (Step 1)
            // https://www.w3.org/TR/webauthn-2/#sctn-op-get-assertion (Step 12)
            return new WebAuthnError({
                message: 'The authenticator was unable to process the specified options, or could not create a new assertion signature',
                code: 'ERROR_AUTHENTICATOR_GENERAL_ERROR',
                cause: error,
            });
        }
        return error;
    }
    isPublicKeyCredentialRequestOptionsJSON(v) {
        return typeof v === "object" && v !== null && "challenge" in v;
    }
    isWebAuthnAvailable() {
        return this.capabilities.webauthn;
    }
    // The browser can signal available credentials on the server to the passkey provider, 
    // so that the passkey provider can keep the passkey list consistent with the server.
    isSignalAllAcceptedCredentialsAvailable() {
        return !!this.capabilities.signalAllAcceptedCredentials;
    }
    // The browser can signal user information such as username and display name on the server to the passkey provider, 
    // so that the passkey provider can keep their passkey information consistent with the server.
    isSignalCurrentUserDetailsAvailable() {
        return !!this.capabilities.signalCurrentUserDetails;
    }
    // The browser can signal a deleted credential on the server to the passkey provider, 
    // so that the passkey provider can keep the passkey list consistent with the server.
    isSignalUnknownCredentialAvailable() {
        return !!this.capabilities.signalUnknownCredential;
    }
    // The browser can authenticate by displaying passkeys as part of autofill dialog, 
    // instead of a prominent modal UI.
    isConditionalGetAvailable() {
        return !!this.capabilities.conditionalGet;
    }
    isConditionalCreateAvailable() {
        return !!this.capabilities.conditionalCreate;
    }
    // the browser can create a credential and authenticate with it cross-device using the hybrid protocol.
    isHybridTransportAvailable() {
        return !!this.capabilities.hybridTransport;
    }
    destroy() {
        controller.abort();
    }
}
/**
 * Visibly warn when we detect an issue related to a passkey provider intercepting WebAuthn API
 * calls
 */
function warnOnBrokenImplementation(methodName, cause) {
    console.warn(`The browser extension that intercepted this WebAuthn API call incorrectly implemented ${methodName}. You should report this error to them.\n`, cause);
}
/**
 * A custom Error used to return a more nuanced error detailing _why_ one of the eight documented
 * errors in the spec was raised after calling `navigator.credentials.create()` or
 * `navigator.credentials.get()`:
 *
 * - `AbortError`
 * - `ConstraintError`
 * - `InvalidStateError`
 * - `NotAllowedError`
 * - `NotSupportedError`
 * - `SecurityError`
 * - `TypeError`
 * - `UnknownError`
 *
 * Error messages were determined through investigation of the spec to determine under which
 * scenarios a given error would be raised.
 */
class WebAuthnError extends Error {
    constructor({ message, code, cause, name, }) {
        super(message);
        this.name = name !== null && name !== void 0 ? name : cause.name;
        this.code = code;
    }
}
function isValidDomain(hostname) {
    return (
    // Consider localhost valid as well since it's okay wrt Secure Contexts
    hostname === 'localhost' ||
        /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i.test(hostname));
}

class AuthService {
    constructor() {
        this.configured = false;
        this.loginflow = null;
        this.navigationManager = null;
        this.flowEventBus = null;
        this.webauthnService = null;
    }
    static get Instance() {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }
    ensureConfigured() {
        if (!this.configured) {
            throw new LoginflowError("NOT_CONFIGURED", "Auth.configure() must be called before using Auth methods");
        }
    }
    ensureLoginflowInitialized() {
        if (!this.loginflow) {
            throw new LoginflowError("LOGINFLOW_NOT_INITIALIZED", "Call Auth.startLoginflow() first");
        }
    }
    startLoginflowInternal(flowId_1) {
        return __awaiter(this, arguments, void 0, function* (flowId, skipHistoryTracking = false) {
            this.ensureConfigured();
            // Cleanup old instances before creating new ones
            if (this.webauthnService) {
                console.log("[AuthService] Cleaning up and aborting ongoing webauthn flows");
                this.webauthnService.destroy();
                this.webauthnService = null;
            }
            if (this.loginflow) {
                console.log("[AuthService] Cleaning up old loginflow instance");
                this.loginflow.cleanup();
                this.loginflow = null;
            }
            if (this.navigationManager) {
                console.log("[AuthService] Cleaning up old navigation manager instance");
                this.navigationManager.destroy();
                this.navigationManager = null;
            }
            this.webauthnService = new WebAuthnService();
            this.webauthnService.detectCapabilities();
            // Create shared event bus for communication between Loginflow and NavigationManager
            this.flowEventBus = new FlowEventBus();
            // Create navigation manager with event bus
            // Pass a callback that restarts the flow when navigation is detected
            this.navigationManager = new NavigationManager(this.flowEventBus, () => __awaiter(this, void 0, void 0, function* () {
                console.log("[AuthService] Navigation restart triggered, restarting loginflow");
                // Don't pass flowId - let it auto-detect from current URL
                // Don't skip history tracking - we need to keep history in sync with session storage
                yield this.startLoginflowInternal(undefined, false);
            }), skipHistoryTracking);
            // Initialize Loginflow with the shared event bus
            const result = yield Loginflow.init(flowId, this.flowEventBus);
            const config = store.getConfig();
            // Attempt to get public key credential options for conditional flow (autofill)
            // TODO: we can parse the render spec to search for inputs that has webauthn attribute to avoid sending this
            // request if the webauthn attribute is not available for autofill
            this.startPasskeyAuthentication(result.flowType, true);
            this.loginflow = result.sdk;
            const response = {
                flowType: result.flowType,
                isResumed: result.isResumed,
                state: result.state,
                render_spec: result.render_spec,
                auth_result: result.auth_result,
            };
            // Emit flow state change event
            if (config.onFlowStateChange) {
                config.onFlowStateChange({
                    eventType: result.isResumed ? "flow_resumed" : "flow_started",
                    flowType: result.flowType,
                    state: result.state,
                    render_spec: result.render_spec,
                    auth_result: result.auth_result,
                    isResumed: result.isResumed,
                });
            }
            return response;
        });
    }
    configure(config) {
        // Validate required fields
        if (!config.domain || !config.appId || !config.clientSecret) {
            throw new LoginflowError("CONFIG_ERROR", "domain, appId, and clientSecret are required");
        }
        store.setConfig(config);
        this.configured = true;
    }
    isAuthenticated() {
        this.ensureConfigured();
        return SessionService.Instance.isAuthenticated();
    }
    getCurrentSession() {
        this.ensureConfigured();
        return SessionService.Instance.getCurrentSession();
    }
    getAuthenticatedUser() {
        this.ensureConfigured();
        return SessionService.Instance.getAuthenticatedUser();
    }
    startLoginflow(flowId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.startLoginflowInternal(flowId, false);
        });
    }
    processLoginflowEvent(request) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            this.ensureConfigured();
            this.ensureLoginflowInitialized();
            const config = store.getConfig();
            const flowType = FlowTypeDetector.detect(config, location);
            // we need to check for credential_request if it's present it means this method is called
            // from autofill authentication ceremony. if it's not present it means it's called by
            // signin with passkey button
            if (request.event === "signin_with_passkey" &&
                !((_a = request.data) === null || _a === void 0 ? void 0 : _a["credential_request"])) {
                const result = yield this.getPasskeyCredential(flowType, false);
                if (!result || !result.cred) {
                    console.error("no credential returned by browser");
                    return { state: "" };
                }
                request.data = { credential_request: result.cred };
            }
            // here we need to check if the request contains conditional=true it means
            // it's triggered by conditional passkey creation flow but if it's not it's triggered from
            // register passkey button
            if (request.event === "register_passkey" &&
                !((_b = request.data) === null || _b === void 0 ? void 0 : _b["conditional"])) {
                console.log("attempt to register a passkey");
                const cred = yield this.createPasskeyCredential(flowType, false);
                if (!cred) {
                    console.error("no credential created by browser");
                    return { state: "" };
                }
                request.data = { credential_creation_response: cred, conditional: false };
            }
            const response = yield this.loginflow.processEvent(request, (res) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if ((_a = res.data) === null || _a === void 0 ? void 0 : _a["conditional_create_enabled"]) {
                    console.log("attempt to register a passkey conditionally");
                    const registerRes = yield this.registerPasskey(flowType, true);
                    if (registerRes) {
                        // we don't show the ui after signup
                        registerRes.render_spec = undefined;
                        res.render_spec = undefined;
                    }
                }
            }));
            console.log("render spec", response.render_spec);
            // Emit flow state change event
            if (config.onFlowStateChange) {
                // Get current flow state from sessionStorage to get flowType
                const flowStateStr = sessionStorage.getItem("saasbase_loginflow_state");
                let flowType = "custom";
                if (flowStateStr) {
                    try {
                        const flowState = JSON.parse(flowStateStr);
                        flowType = flowState.flowType;
                    }
                    catch (e) {
                        console.error("Failed to parse flow state:", e);
                    }
                }
                config.onFlowStateChange({
                    eventType: "flow_updated",
                    flowType: flowType,
                    state: response.state,
                    render_spec: response.render_spec,
                    auth_result: response.auth_result,
                });
            }
            return response;
        });
    }
    getPasskeyCredential(flowType, useBrowserAutofill) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const options = yield ((_a = this.webauthnService) === null || _a === void 0 ? void 0 : _a.fetchPublicKeyCredentialRequestOptions(flowType, useBrowserAutofill));
            if (!options) {
                console.error("failed to fetch public key request options");
                return null;
            }
            try {
                const cred = yield ((_b = this.webauthnService) === null || _b === void 0 ? void 0 : _b.startAuthentication(options, useBrowserAutofill));
                return { cred, options };
            }
            catch (err) {
                console.error("error starting authentication", err);
                return null;
            }
        });
    }
    startPasskeyAuthentication(flowType_1) {
        return __awaiter(this, arguments, void 0, function* (flowType, useBrowserAutofill = false) {
            var _a, _b;
            const result = yield this.getPasskeyCredential(flowType, useBrowserAutofill);
            if (!result || !result.cred) {
                console.error("no credential found");
                return;
            }
            const { cred, options } = result;
            try {
                const resp = yield this.processLoginflowEvent({
                    event: "signin_with_passkey",
                    data: { credential_request: cred },
                });
                if (((_a = resp.data) === null || _a === void 0 ? void 0 : _a["error"]) === "CredentialNotFound") {
                    if (options.rpId && cred.id) {
                        (_b = this.webauthnService) === null || _b === void 0 ? void 0 : _b.signalUnknownCredential(options.rpId, cred.id);
                    }
                    else {
                        console.log("missing rpId or credential id for signalUnknownCredential");
                    }
                }
            }
            catch (err) {
                console.error("failed to signin with passkey", err);
            }
        });
    }
    createPasskeyCredential(flowType, conditional) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                const res = yield ((_a = this.webauthnService) === null || _a === void 0 ? void 0 : _a.fetchPublicKeyCredentialCreationOptions(flowType, conditional));
                if (!res) {
                    console.warn("no credential creation option returned by webauthn service");
                    console.log("skipping conditional passkey registration...");
                    return;
                }
                return (_b = this.webauthnService) === null || _b === void 0 ? void 0 : _b.startRegistration(res, conditional);
            }
            catch (error) {
                console.log("error", error);
            }
        });
    }
    registerPasskey(flowType_1) {
        return __awaiter(this, arguments, void 0, function* (flowType, conditional = false) {
            try {
                const cred = yield this.createPasskeyCredential(flowType, conditional);
                const response = yield this.processLoginflowEvent({
                    event: "register_passkey",
                    data: {
                        credential_creation_response: cred,
                        conditional: conditional,
                    },
                });
                console.log("passkey creation res", response);
                return response;
            }
            catch (error) {
                console.log("error", error);
            }
        });
    }
    signOut() {
        this.ensureConfigured();
        // Clear session
        SessionService.Instance.clearSession();
        SessionService.Instance.cleanup();
        // Clear loginflow instance and cancel any pending operations
        if (this.loginflow) {
            this.loginflow.cleanup();
            this.loginflow = null;
        }
        // Clear navigation manager
        if (this.navigationManager) {
            this.navigationManager.destroy();
            this.navigationManager = null;
        }
        // Clear event bus
        if (this.flowEventBus) {
            this.flowEventBus.clear();
            this.flowEventBus = null;
        }
        // Refresh the page to clear any in-memory state
        window.location.reload();
        console.log("Refreshed the page to clear any in-memory state");
    }
}

const Auth = AuthService.Instance;

export { Auth, LoginflowError };
//# sourceMappingURL=index.esm.js.map
